/**
 * Typed, runtime-validated view of `import.meta.env`.
 *
 * Application code MUST import environment values from this module — not from
 * `import.meta.env` directly — so that missing required variables fail loudly
 * at startup instead of producing `undefined` deep in a call site.
 */

type AppEnvironment = 'development' | 'staging' | 'production'

const APP_ENV_RAW = import.meta.env.VITE_APP_ENV
const IS_PRODUCTION_LIKE =
  APP_ENV_RAW === 'production' || APP_ENV_RAW === 'staging'

function requireEnv(name: keyof ImportMetaEnv): string {
  const value = import.meta.env[name]
  if (typeof value !== 'string' || value.length === 0) {
    throw new Error(
      `Missing required env var: ${String(name)}. ` +
        'Set it in the appropriate .env file for this build mode ' +
        '(e.g. .env.development, .env.staging, .env.production).',
    )
  }
  return value
}

function requireEnvInNonDev(name: keyof ImportMetaEnv): string {
  const value = import.meta.env[name]
  if (typeof value !== 'string' || value.length === 0) {
    if (IS_PRODUCTION_LIKE) {
      throw new Error(
        `Missing required env var: ${String(name)} ` +
          `(required in staging and production builds; current VITE_APP_ENV=${String(APP_ENV_RAW)}).`,
      )
    }
    return ''
  }
  return value
}

function assertAppEnvironment(value: string | undefined): AppEnvironment {
  if (value === 'development' || value === 'staging' || value === 'production') {
    return value
  }
  throw new Error(
    `Invalid VITE_APP_ENV: ${String(value)}. ` +
      "Expected one of 'development' | 'staging' | 'production'.",
  )
}

export const VITE_APP_ENV: AppEnvironment = assertAppEnvironment(APP_ENV_RAW)

export const VITE_API_BASE_URL: string = requireEnv('VITE_API_BASE_URL')

/**
 * Stripe publishable key.
 *
 * Required in staging / production. In development we fall back to an empty
 * string so developers can run `pnpm dev` without provisioning a Stripe key.
 */
export const VITE_STRIPE_PUBLISHABLE_KEY: string = requireEnvInNonDev(
  'VITE_STRIPE_PUBLISHABLE_KEY',
)

/**
 * GA4 measurement ID. Same optionality rules as Stripe key.
 */
export const VITE_GA4_MEASUREMENT_ID: string = requireEnvInNonDev(
  'VITE_GA4_MEASUREMENT_ID',
)

/**
 * App version. Falls back to a stub rather than throwing so local dev
 * without a CI-injected git SHA still boots.
 */
export const VITE_APP_VERSION: string =
  import.meta.env.VITE_APP_VERSION && import.meta.env.VITE_APP_VERSION.length > 0
    ? import.meta.env.VITE_APP_VERSION
    : '0.0.0-dev'

export const env = {
  VITE_API_BASE_URL,
  VITE_STRIPE_PUBLISHABLE_KEY,
  VITE_GA4_MEASUREMENT_ID,
  VITE_APP_ENV,
  VITE_APP_VERSION,
} as const

export type AppEnv = typeof env
